import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useUpdateAidStation } from '../../hooks/useAidStations'
import { useSegments } from '../../hooks/useSegments'
import { validateAidStationDistance } from '../../lib/calculations'
import { AidStation } from '../../types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'

const aidStationSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  at_distance_km: z.number().min(0, 'Distance must be positive'),
  planned_stop_min: z.number().min(0).max(300).optional(),
  available: z.string().optional(),
  notes: z.string().optional(),
})

type AidStationForm = z.infer<typeof aidStationSchema>

interface EditAidStationDialogProps {
  aidStation: AidStation | null
  runId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditAidStationDialog({ aidStation, runId, open, onOpenChange }: EditAidStationDialogProps) {
  const [error, setError] = useState<string | null>(null)
  const updateMutation = useUpdateAidStation()
  const { data: segments } = useSegments(runId)
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<AidStationForm>({
    resolver: zodResolver(aidStationSchema),
    defaultValues: {
      planned_stop_min: 0
    }
  })

  // Reset form when aidStation changes
  useEffect(() => {
    if (aidStation) {
      reset({
        name: aidStation.name,
        at_distance_km: aidStation.at_distance_km,
        planned_stop_min: aidStation.planned_stop_min || 0,
        available: Array.isArray(aidStation.available) ? aidStation.available.join(', ') : '',
        notes: aidStation.notes || '',
      })
    }
  }, [aidStation, reset])

  const onSubmit = async (data: AidStationForm) => {
    if (!aidStation) return
    
    setError(null)

    // Validate distance
    if (segments) {
      const validation = validateAidStationDistance(data.at_distance_km, segments)
      if (!validation.isValid) {
        setError(validation.message || 'Invalid distance')
        return
      }
    }

    try {
      const availableItems = data.available
        ? data.available.split(',').map(item => item.trim()).filter(Boolean)
        : []

      await updateMutation.mutateAsync({
        id: aidStation.id,
        updates: {
          name: data.name,
          at_distance_km: data.at_distance_km,
          planned_stop_min: data.planned_stop_min || 0,
          available: availableItems,
          notes: data.notes || null,
        }
      })

      onOpenChange(false)
    } catch (err) {
      setError('Failed to update aid station')
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setError(null)
    }
    onOpenChange(newOpen)
  }

  if (!aidStation) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Aid Station</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="e.g., Mile 20 Aid Station"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="at_distance_km">Distance (km)</Label>
            <Input
              id="at_distance_km"
              type="number"
              step="0.1"
              {...register('at_distance_km', { valueAsNumber: true })}
              placeholder="32.2"
            />
            {errors.at_distance_km && (
              <p className="text-sm text-red-500">{errors.at_distance_km.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="planned_stop_min">Planned Stop (minutes)</Label>
            <Input
              id="planned_stop_min"
              type="number"
              {...register('planned_stop_min', { valueAsNumber: true })}
              placeholder="5"
            />
            {errors.planned_stop_min && (
              <p className="text-sm text-red-500">{errors.planned_stop_min.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="available">Available Items (comma-separated)</Label>
            <Input
              id="available"
              {...register('available')}
              placeholder="Water, Sports drink, Gels, Bananas"
            />
            <p className="text-xs text-gray-500">
              List items available at this aid station, separated by commas
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Any additional notes about this aid station..."
              rows={3}
            />
          </div>

          {error && (
            <div className="text-sm text-red-500 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Updating...' : 'Update Aid Station'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}